import { useLingui } from "@lingui/react/macro";
import { cn } from "@/lib/utils";
import { ingredientLineName } from "@/types/recipe";
import { kitchenAmount, type BatchScale, type MethodGroup } from "@/lib/recipes/staged-method";
import { stageMark, type StageMark } from "@/lib/multi-view";
import type { CookingSession } from "@/types/cooking-session";
import { useSessionMethod } from "./useSessionMethod";

interface StageListProps {
  session: CookingSession;
  scale: BatchScale;
  /** Compact cards show only the stage being cooked, with its ingredients. */
  onlyCurrent?: boolean;
  className?: string;
}

function MarkTag({ mark }: { mark: StageMark }) {
  const { t } = useLingui();
  if (mark === "upcoming") return null;
  return (
    <span
      className={cn(
        "rounded-full px-2 py-0.5 text-[0.6875rem] font-bold tracking-wide uppercase",
        mark === "now"
          ? "bg-(--brand-blue) text-(--brand-white)"
          : "bg-(--color-surface-accent) text-(--color-text-secondary)",
      )}
    >
      {mark === "now" ? t`Now` : t`Done`}
    </span>
  );
}

/**
 * A recipe's stages with the ingredients beside the procedure, the stage being
 * cooked tagged NOW and the ones behind it DONE. Stage positions are the
 * recipe's STAGE rows in order, which is the position the API records.
 */
export function StageList({ session, scale, onlyCurrent = false, className }: StageListProps) {
  const { t } = useLingui();
  const { method, isLoading } = useSessionMethod(session.recipeId);

  if (!method) {
    return (
      <p className={cn("text-sm text-(--color-text-secondary)", className)}>{isLoading ? t`Loading...` : t`No method to show.`}</p>
    );
  }

  const entries = method.groups.map((group, index) => ({
    group,
    // Only a real stage has a position; the leftover group (no stage) has none.
    position: group.stage ? index : null,
  }));
  const currentPosition = session.currentStage?.position ?? null;
  // A recipe not started yet shows its first stage, so the card is never empty.
  const visible = onlyCurrent
    ? entries.filter((e) => (currentPosition === null ? e === entries[0] : e.position === currentPosition))
    : entries;

  return (
    <div className={cn("flex flex-col gap-3", className)}>
      {visible.map(({ group, position }) => {
        const mark = position === null ? "upcoming" : stageMark(session, position);
        return (
          <section
            key={group.stage?.id ?? "unstaged"}
            aria-label={group.stage?.action ?? t`Method`}
            className={cn(
              "flex flex-col gap-2 rounded-xl border p-3",
              mark === "now" ? "border-(--brand-blue)" : "border-(--color-border)",
              mark === "done" && "opacity-60",
            )}
          >
            <header className="flex items-center gap-2">
              <h4 className="text-sm font-semibold text-(--color-text-primary)">{group.stage?.action ?? t`Method`}</h4>
              <MarkTag mark={mark} />
            </header>
            <GroupBody group={group} scale={scale} showSteps={!onlyCurrent} />
          </section>
        );
      })}
    </div>
  );
}

function GroupBody({ group, scale, showSteps }: { group: MethodGroup; scale: BatchScale; showSteps: boolean }) {
  const { t } = useLingui();
  return (
    <div className={cn("grid gap-3", showSteps && group.steps.length > 0 && "sm:grid-cols-2")}>
      <ul className="flex flex-col gap-1 text-sm text-(--color-text-primary)" aria-label={t`Ingredients`}>
        {group.lines.length === 0 ? (
          <li className="text-(--color-text-secondary)">{t`No ingredients join here.`}</li>
        ) : (
          group.lines.map((line) => (
            <li key={line.id} className="flex justify-between gap-2">
              <span className="min-w-0 truncate">{ingredientLineName(line)}</span>
              <span className="shrink-0 font-medium tabular-nums">{kitchenAmount(Number(line.quantity) * scale, line.unit)}</span>
            </li>
          ))
        )}
      </ul>
      {showSteps && group.steps.length > 0 && (
        <ol className="flex flex-col gap-1 text-sm text-(--color-text-primary)" aria-label={t`Procedure`}>
          {group.steps.map((step) => (
            <li key={step.number} className="flex gap-2">
              <span className="font-semibold text-(--color-text-secondary)">{step.number}.</span>
              <span>{step.text}</span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
